import { useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { EllipsisVertical, Eye, Pencil, Trash2 } from "lucide-react";
import { Popover } from "radix-ui";

import { cn } from "@/libs/cn";
import { ROUTES } from "@/routes/constants/routes";

interface ReleaseNoteActionsProps {
  id: string;
  canWrite: boolean;
  onView: () => void;
  onDelete: () => void;
}

const ReleaseNoteActions = ({ id, canWrite, onView, onDelete }: ReleaseNoteActionsProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const choose = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  const items = [
    { label: "Visualizar", icon: Eye, onSelect: onView, visible: true, danger: false },
    {
      label: "Editar",
      icon: Pencil,
      onSelect: () => navigate(generatePath(ROUTES.RELEASE_NOTES.EDIT, { id })),
      visible: canWrite,
      danger: false,
    },
    { label: "Excluir", icon: Trash2, onSelect: onDelete, visible: canWrite, danger: true },
  ].filter((item) => item.visible);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-label="Ações"
        onClick={(event) => event.stopPropagation()}
        className="flex size-8 items-center justify-center rounded-lg border border-ui-border bg-white text-ui-text-muted transition hover:bg-gray-50 cursor-pointer"
      >
        <EllipsisVertical size={16} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          onClick={(event) => event.stopPropagation()}
          className="z-50 w-44 rounded-xl border border-ui-border bg-white py-1 shadow-lg"
        >
          {items.map(({ label, icon: Icon, onSelect, danger }) => (
            <button
              key={label}
              type="button"
              onClick={choose(onSelect)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-gray-50 cursor-pointer",
                danger ? "text-[#EC221F]" : "text-ui-primary",
              )}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default ReleaseNoteActions;
